import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface Tier {
  name: string;
  price: string;
}

interface Module {
  title: string;
  description: string;
}

interface PrimaryModel {
  label: string;
  title: string;
  description: string;
  tiers: Tier[];
}

interface SecondaryModel {
  label: string;
  title: string;
  description: string;
  modules: Module[];
}

export function Monetization() {
  const { t, tObject } = useTranslation();
  const primaryModel = tObject<PrimaryModel>('monetization.primaryModel');
  const secondaryModel = tObject<SecondaryModel>('monetization.secondaryModel');

  return (
    <section id="revenue" className="bg-slate-100 dark:bg-[#0d1318] py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('monetization.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl tracking-tight">
            {t('monetization.title')}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Model 1: SaaS Licenses */}
          <motion.div
            className="relative p-10 lg:p-12 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white dark:bg-[#0a0f14] transition-all duration-300 hover:shadow-lg"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-linear-to-r from-[#5a8a9a] to-transparent" />

            <div className="flex items-start justify-between mb-8">
              <div>
                <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-sm tracking-wider uppercase font-medium">
                  {primaryModel?.label}
                </span>
                <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-2xl md:text-3xl mt-2">
                  {primaryModel?.title}
                </h3>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl border border-slate-300 dark:border-[#3d5a6e]">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="2"
                    stroke="#7ab8c9"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <path d="M3 9h18M9 21V9" stroke="#7ab8c9" strokeWidth="1.5" />
                </svg>
              </div>
            </div>

            <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed mb-8">
              {primaryModel?.description}
            </p>

            <div className="space-y-4">
              {primaryModel?.tiers.map((tier, index) => (
                <div
                  key={tier.name}
                  className={`flex items-center justify-between py-3 ${index < (primaryModel?.tiers.length ?? 0) - 1
                      ? 'border-b border-slate-200 dark:border-[#1a2530]'
                      : ''
                    }`}
                >
                  <span className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base">
                    {tier.name}
                  </span>
                  <span className="text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base font-medium">
                    {tier.price}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Model 2: Premium Analytics */}
          <motion.div
            className="relative p-10 lg:p-12 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white dark:bg-[#0a0f14] transition-all duration-300 hover:shadow-lg"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-linear-to-r from-slate-400 dark:from-[#3d5a6e] to-transparent" />

            <div className="flex items-start justify-between mb-8">
              <div>
                <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-sm tracking-wider uppercase font-medium">
                  {secondaryModel?.label}
                </span>
                <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-2xl md:text-3xl mt-2">
                  {secondaryModel?.title}
                </h3>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl border border-slate-300 dark:border-[#3d5a6e]">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path
                    d="M3 20L8 15L13 18L21 10"
                    stroke="#7ab8c9"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <circle cx="8" cy="15" r="2" fill="#7ab8c9" opacity="0.5" />
                  <circle cx="13" cy="18" r="2" fill="#7ab8c9" opacity="0.5" />
                  <circle cx="21" cy="10" r="2" fill="#7ab8c9" opacity="0.5" />
                </svg>
              </div>
            </div>

            <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed mb-8">
              {secondaryModel?.description}
            </p>

            <div className="space-y-4">
              {secondaryModel?.modules.map((module, index) => (
                <div
                  key={module.title}
                  className={`flex items-start gap-3 py-3 ${index < (secondaryModel?.modules.length ?? 0) - 1
                      ? 'border-b border-slate-200 dark:border-[#1a2530]'
                      : ''
                    }`}
                >
                  <div className="w-1.5 h-1.5 mt-1.5 bg-[#5a8a9a]" />
                  <div>
                    <span className="text-slate-900 dark:text-[#e8ebe9] text-sm md:text-base block font-medium">
                      {module.title}
                    </span>
                    <span className="text-slate-500 dark:text-[#6a7a7e] text-xs md:text-sm">
                      {module.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="mt-12 p-6 lg:p-8 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0a0f14]/50"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-slate-500 dark:text-[#6a7a7e] text-sm md:text-base text-center leading-relaxed">
            {t('monetization.footer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
